import React, { useState } from 'react';
import type { DetectionResult, PageFacts, SchemaScore } from '../types';
import { hasExistingOfType } from '../lib/existing';
import { schemaTypeIcon, FactIcons } from '../lib/icons';
import { ScoreCard } from './ScoreCard';

const ALL_TYPES = [
  'CreativeWork', 'Article', 'NewsArticle', 'BlogPosting', 'Book', 'Movie',
  'TVSeries', 'MusicRecording', 'Recipe', 'AudioObject', 'ImageObject',
  'VideoObject', 'Event', 'Organization', 'Person', 'Place', 'LocalBusiness',
  'Restaurant', 'Product', 'Offer', 'AggregateOffer', 'Review', 'AggregateRating',
  'FAQPage', 'HowTo', 'JobPosting', 'Course', 'BreadcrumbList',
];

interface DetectionResultsProps {
  detection: DetectionResult;
  facts: PageFacts;
  score: SchemaScore | null;
  busy: boolean;
  /** When non-null, only the card matching this type shows its spinner */
  generatingType: string | null;
  /** Progress for the "Generate all" run, if active */
  bulkProgress: { current: number; total: number } | null;
  /** Types already in the @graph — show "Generated" badge on those candidates */
  generatedTypes: string[];
  onGenerate: (typeName: string, augment: boolean) => void;
  onGenerateAll: () => void;
}

export const DetectionResults: React.FC<DetectionResultsProps> = ({
  detection,
  facts,
  score,
  busy,
  generatingType,
  bulkProgress,
  generatedTypes,
  onGenerate,
  onGenerateAll,
}) => {
  const [overrideType, setOverrideType] = useState<string>('');
  const [showAll, setShowAll] = useState(false);

  const generatedSet = new Set(generatedTypes);
  const candidateCount = detection.candidates.length;
  const isBulkRunning = bulkProgress !== null;

  const handleSubmitOverride = () => {
    if (!overrideType) return;
    const canAugment = hasExistingOfType(facts.existingJsonLd, overrideType);
    onGenerate(overrideType, canAugment);
  };

  return (
    <div>
      {score && <ScoreCard score={score} />}

      <div style={{ marginBottom: 32 }}>
        <span className="eyebrow">Page read</span>
        <p className="lead" style={{ marginTop: 8, marginBottom: 0, fontSize: 17 }}>
          {detection.pageRead}
        </p>

        {detection.existingTypes.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <span className="eyebrow" style={{ display: 'block', marginBottom: 10 }}>
              Already on the page
            </span>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {detection.existingTypes.map((t) => (
                <span key={t} className="type-badge" title={`${t} JSON-LD detected on the page`}>
                  <span className="type-icon-circle" style={{ width: 22, height: 22 }}>
                    {schemaTypeIcon(t, { size: 13 })}
                  </span>
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 className="h2" style={{ marginBottom: 6 }}>Suggested types</h2>
          <p className="meta" style={{ margin: 0 }}>
            Pick one or generate the whole high-confidence set as a single <code>@graph</code>.
          </p>
        </div>
        {candidateCount > 0 && (
          <button
            type="button"
            className="btn btn-signal"
            onClick={onGenerateAll}
            disabled={busy}
          >
            {isBulkRunning ? (
              <>
                <span className="spinner" />
                Generating {bulkProgress!.current}/{bulkProgress!.total}
                {generatingType ? ` · ${generatingType}` : ''}…
              </>
            ) : (
              <>
                <SparkleIcon /> Generate all {candidateCount} as @graph
              </>
            )}
          </button>
        )}
      </div>
      <p className="meta" style={{ marginBottom: 24 }}>
        Confidence shows how well the page content fits the type. "Generate all" creates a merged{' '}
        <code>@graph</code> in one go — augmenting any types already on the page.
      </p>

      {detection.candidates.length === 0 ? (
        <div className="card">
          <p style={{ margin: 0, color: 'var(--ink-3)' }}>
            No high-confidence matches. Try the manual picker below.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {detection.candidates.map((c) => (
            <CandidateCard
              key={c.type}
              candidate={c}
              busy={busy}
              activeOnThisCard={generatingType === c.type}
              hasExisting={hasExistingOfType(facts.existingJsonLd, c.type)}
              alreadyGenerated={generatedSet.has(c.type)}
              onGenerate={(augment) => onGenerate(c.type, augment)}
            />
          ))}
        </div>
      )}

      <div style={{ marginTop: 32 }}>
        <button
          type="button"
          className="btn btn-sm"
          style={{ background: 'transparent', color: 'var(--signal)', padding: '6px 0' }}
          onClick={() => setShowAll((v) => !v)}
        >
          {showAll ? 'Hide manual picker' : 'Pick a different type manually'}
        </button>
        {showAll && (
          <div className="card" style={{ marginTop: 12 }}>
            <p className="meta" style={{ marginBottom: 12 }}>
              Override the suggestion. Use this when you know the page better than the detector.
            </p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <select
                className="input"
                style={{ flex: 1, minWidth: 220, fontFamily: 'var(--font-body)', fontSize: 14 }}
                value={overrideType}
                onChange={(e) => setOverrideType(e.target.value)}
              >
                <option value="">— Choose a Schema.org type —</option>
                {ALL_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmitOverride}
                disabled={busy || !overrideType}
              >
                {busy ? <><span className="spinner" /> Generating…</> : 'Generate'}
              </button>
            </div>
          </div>
        )}
      </div>

      <FactsSummary facts={facts} />
    </div>
  );
};

const CandidateCard: React.FC<{
  candidate: DetectionResult['candidates'][number];
  busy: boolean;
  activeOnThisCard: boolean;
  hasExisting: boolean;
  alreadyGenerated: boolean;
  onGenerate: (augment: boolean) => void;
}> = ({ candidate: c, busy, activeOnThisCard, hasExisting, alreadyGenerated, onGenerate }) => {
  const conf = Math.round(c.confidence);
  const fillClass =
    conf >= 80 ? 'is-high' : conf >= 60 ? 'is-mid' : 'is-low';
  const matchTone =
    conf >= 80 ? 'pill-good' : conf >= 60 ? 'pill-info' : 'pill-warning';
  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          {/* Title row: icon + type + match pill + on-page pill */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <span className="type-icon-circle" aria-hidden>
              {schemaTypeIcon(c.type, { size: 16 })}
            </span>
            <h3 className="h3" style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 18 }}>
              {c.type}
            </h3>
            <span className={`pill ${matchTone}`}>{conf}% match</span>
            {hasExisting && (
              <span className="pill pill-info" title="JSON-LD of this type already exists on the page">
                On page
              </span>
            )}
            {alreadyGenerated && (
              <span className="pill pill-good" title="Already in your generated @graph">
                Generated
              </span>
            )}
          </div>

          {/* Slim confidence bar — visual reinforcement of the pill */}
          <span className="confidence-track" style={{ marginBottom: 12, maxWidth: 360 }}>
            <span className={`confidence-fill ${fillClass}`} style={{ width: `${conf}%` }} />
          </span>

          <p style={{ marginTop: 0, marginBottom: 12, color: 'var(--ink-2)', fontSize: 14, lineHeight: 1.55 }}>
            {c.rationale}
          </p>

          {c.presentSignals.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: 6 }}>Page signals</span>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
                {c.presentSignals.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ul>
            </div>
          )}
          {c.missingRequired.length > 0 && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--score-mid-wash)', border: '1px solid rgba(181,132,42,0.3)', borderRadius: 4 }}>
              <span className="eyebrow" style={{ display: 'block', marginBottom: 4, color: 'var(--score-mid)' }}>
                Missing required fields
              </span>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-2)' }}>
                {c.missingRequired.map((f) => <code key={f} style={{ marginRight: 6 }}>{f}</code>)}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--ink-3)' }}>
                You'll need to provide these manually after generation.
              </p>
            </div>
          )}
        </div>
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'stretch', minWidth: 168 }}>
          {hasExisting ? (
            <>
              <button
                type="button"
                className="btn btn-signal"
                onClick={() => onGenerate(true)}
                disabled={busy}
                title="Use the page's existing JSON-LD as a base; only fill in missing fields. Output is a strict superset."
              >
                {activeOnThisCard ? <><span className="spinner" /> Working…</> : (
                  <>
                    <RefreshIcon /> Augment existing
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => onGenerate(false)}
                disabled={busy}
                title="Ignore the existing JSON-LD and generate from scratch."
              >
                Generate fresh
              </button>
            </>
          ) : alreadyGenerated ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => onGenerate(false)}
              disabled={busy}
              title="Replace the existing generated entity for this type"
            >
              {activeOnThisCard ? <><span className="spinner" /> Regenerating…</> : 'Regenerate'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onGenerate(false)}
              disabled={busy}
            >
              {activeOnThisCard ? <><span className="spinner" /> Generating…</> : (
                <>
                  <SparkleIcon /> Generate JSON-LD
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

type FactItem = {
  label: string;
  value: string | number;
  icon: React.ReactElement;
};

const FactsSummary: React.FC<{ facts: PageFacts }> = ({ facts }) => {
  const items: FactItem[] = [];
  if (facts.headings.length) items.push({ label: 'Headings', value: facts.headings.length, icon: <FactIcons.Heading size={16} /> });
  if (facts.images.length) items.push({ label: 'Images', value: facts.images.length, icon: <FactIcons.Image size={16} /> });
  if (facts.video.length) items.push({ label: 'Videos', value: facts.video.length, icon: <FactIcons.Video size={16} /> });
  if (facts.audio.length) items.push({ label: 'Audio', value: facts.audio.length, icon: <FactIcons.Audio size={16} /> });
  if (facts.addresses.length) items.push({ label: 'Addresses', value: facts.addresses.length, icon: <FactIcons.Address size={16} /> });
  if (facts.phones.length) items.push({ label: 'Phone numbers', value: facts.phones.length, icon: <FactIcons.Phone size={16} /> });
  if (facts.emails.length) items.push({ label: 'Emails', value: facts.emails.length, icon: <FactIcons.Email size={16} /> });
  if (facts.priceCandidates.length) items.push({ label: 'Price hints', value: facts.priceCandidates.slice(0, 2).join(', '), icon: <FactIcons.Price size={16} /> });
  if (facts.times.length) items.push({ label: 'Date/time tags', value: facts.times.length, icon: <FactIcons.Date size={16} /> });
  if (facts.breadcrumbs.length) items.push({ label: 'Breadcrumb links', value: facts.breadcrumbs.length, icon: <FactIcons.Breadcrumb size={16} /> });
  if (facts.existingTypes.length) items.push({ label: 'Existing schema', value: facts.existingTypes.length, icon: <FactIcons.Schema size={16} /> });

  const head: FactItem[] = [];
  if (facts.title) head.push({ label: 'Title', value: facts.title, icon: <FactIcons.Code size={16} /> });
  if (facts.metaDescription) head.push({ label: 'Description', value: facts.metaDescription, icon: <FactIcons.Code size={16} /> });
  if (facts.og['type']) head.push({ label: 'og:type', value: facts.og['type'], icon: <FactIcons.Code size={16} /> });

  if (items.length === 0 && head.length === 0) return null;

  return (
    <details style={{ marginTop: 32 }}>
      <summary style={{ cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 14, color: 'var(--ink-3)' }}>
        What we found on the page
      </summary>
      <div style={{ marginTop: 16 }}>
        {head.length > 0 && (
          <div className="fact-grid" style={{ marginBottom: 12, gridTemplateColumns: '1fr' }}>
            {head.map((it) => (
              <FactCard key={it.label} item={it} />
            ))}
          </div>
        )}
        {items.length > 0 && (
          <div className="fact-grid">
            {items.map((it) => (
              <FactCard key={it.label} item={it} />
            ))}
          </div>
        )}
      </div>
    </details>
  );
};

const RefreshIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const SparkleIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
  </svg>
);

const FactCard: React.FC<{ item: FactItem }> = ({ item }) => (
  <div className="fact-card">
    <span className="fact-card-icon" aria-hidden>{item.icon}</span>
    <div style={{ minWidth: 0, flex: 1 }}>
      <p className="fact-card-label">{item.label}</p>
      <div className="fact-card-value">{String(item.value).slice(0, 140)}</div>
    </div>
  </div>
);
